import { forwardRef } from 'react';
import { oneLine } from 'common-tags';

import Spinner from '@/icons/light/refresh-ccw.svg';

import '@/lib/utils/forwardRef';

import { ButtonSize, ButtonVariant } from './types';

import type { ComponentPropsWithRef, ElementType, Ref } from 'react';
import type { ButtonProps } from './types';

/**
 * Hover de los botones sólidos/outline: lee el token de tema
 * `--tw-color-button-bg-hover` (campo `buttonBgHover` en Strapi → emitido por
 * theme-config.js SOLO si el tenant lo define). Si la var no existe, cae al
 * color de hover histórico de cada variante → cero regresión para tenants que
 * no lo piquen. Mismo esquema para el texto de SECONDARY: token
 * `--tw-color-button-text` (campo `buttonTextColor`), con fallback al texto
 * oscuro histórico. Las clases arbitrarias se escriben literales (no
 * interpoladas) para que el scanner de Tailwind v4 las detecte. Variantes
 * claras (ELEVATED/ALTERNATIVE) se dejan intactas.
 */
const VARIANT_MAPS: Record<ButtonVariant, string> = {
  [ButtonVariant.PRIMARY]:
    'bg-primary-500 text-neutrals-0 hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-primary-600)))]',
  [ButtonVariant.SECONDARY]:
    'bg-secondary-500 hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-400)))] text-[rgb(var(--tw-color-button-text,var(--tw-color-text-neutrals-900)))]',
  [ButtonVariant.ELEVATED]:
    'bg-neutrals-0 hover:bg-primary-50 text-text-neutrals-900',
  [ButtonVariant.ALTERNATIVE]:
    'bg-neutrals-0 hover:bg-primary-50 border-primary-600 text-primary-600',
  [ButtonVariant.OUTLINE]:
    'bg-transparent border-2 border-primary-500! text-primary-500 hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-primary-500)))] hover:text-neutrals-0',
};

const SIZE_MAPS: Record<ButtonSize, string> = {
  [ButtonSize.SMALL]: 'py-2.5',
  [ButtonSize.MEDIUM]: 'py-3',
  [ButtonSize.LARGE]: 'py-[18px]',
};

const TEXT_LINK =
  'text-button text-primary-500 bg-neutrals-0 hover:bg-primary-50! hover:text-primary-500 disabled:bg-primary-50!';

const ButtonBase = <T extends ElementType = 'button'>(
  {
    className = '',
    variant,
    size,
    as,
    children,
    outline = false,
    link = false,
    loading = false,
    trailingIcon,
    ...props
  }: ButtonProps<T> & Omit<ComponentPropsWithRef<T>, keyof ButtonProps<T>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: Ref<any>,
) => {
  const HTMLTag = as || 'button';
  const OUTLINE =
    variant === ButtonVariant.PRIMARY
      ? 'bg-transparent! border-solid border-primary-500 text-button text-primary-500! hover:bg-primary-50! hover:text-primary-500 hover:border-primary-500 disabled:bg-primary-50! disabled:border-none disabled:opacity-100'
      : variant === ButtonVariant.ELEVATED
        ? 'bg-transparent! border-solid border-neutrals-900 text-button text-neutrals-900! hover:text-primary-900! hover:bg-primary-50! hover:text-neutrals-800 disabled:bg-transparent! disabled:border-none disabled:opacity-100'
        : '';

  return (
    <HTMLTag
      className={oneLine`rounded-button text-button flex w-full min-w-[120px] cursor-pointer items-center justify-center gap-x-3 rounded border px-4 text-center transition-colors duration-300 ease-in-out disabled:pointer-events-none disabled:opacity-30 aria-disabled:pointer-events-none aria-disabled:opacity-30 md:w-auto ${VARIANT_MAPS[variant]} ${SIZE_MAPS[size]} ${outline ? OUTLINE : 'border-transparent'} ${link ? TEXT_LINK : ''} ${className ?? ''} `}
      ref={ref}
      {...props}
    >
      {loading ? (
        <Spinner className="h-4 w-4 animate-spin motion-reduce:animate-none" />
      ) : (
        <>
          {children}
          {(() => {
            /* Sin chevron implícito: en legacy los botones secondary/outline
               no llevaban icono. Quien lo quiera lo pasa por trailingIcon
               (p.ej. CardFlat vía el knob listingCardCtaIcon). */
            if (!trailingIcon) return null;
            const IconComponent = trailingIcon;
            return <IconComponent className="h-4 w-4 shrink-0" />;
          })()}
        </>
      )}
    </HTMLTag>
  );
};

export const Button = forwardRef(ButtonBase);
